import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faCircleCheck,
  faEye,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  flipProviderMigrationOwnership,
  previewProviderMigrationOwnership,
  type ProviderMigrationOwnershipResultDto,
  type ProviderMigrationOwnershipStateDto,
} from '../api/provider-migrations.api';
import { parseMigrationError, type ParsedMigrationError } from '../migration-errors';
import { MigrationErrorNotice } from './MigrationErrorNotice';

/**
 * THE MISSING STEP — move ownership from the source provider to the target.
 *
 * ── WHY THIS PANEL EXISTS ──────────────────────────────────────────────────
 * A provider migration copies objects, verifies them, and THEN moves
 * `storage_assets.provider_id`. That third step is what makes the target
 * provider authoritative for those assets, and finalization gate
 * `ASSET_NOT_ON_TARGET_PROVIDER` refuses until it has happened.
 *
 * The console previously had no control for it at all. It went Run -> Finalize,
 * so every migration driven from the UI reached a 409 that the operator could
 * neither read nor resolve, and the step had to be run out-of-band by an
 * engineer. This panel is that step.
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE ───────────────────────────────
 * `flipProviderMigrationOwnership()` is called from EXACTLY ONE place: the
 * confirm handler of the dialog below, which cannot run until a human has opened
 * the dialog and pressed confirm. There is deliberately:
 *
 *   • no `useEffect` in this file — nothing fires on mount, render or update;
 *   • no polling, interval, timeout, retry or background task;
 *   • no auto-preview and no auto-flip; the rehearsal is a click too;
 *   • no path from loading or refreshing the page to either POST.
 *
 * Those absences are the enforcement. A reviewer can confirm it by grepping this
 * file for either function name — one call site each.
 *
 * ── THE REHEARSAL IS NOT A PREREQUISITE, AND IS NOT A PROMISE ──────────────
 * `previewProviderMigrationOwnership()` writes nothing and is offered first
 * because it answers the operator's question — how many objects would actually
 * move — before anything is committed. It is NOT treated as an authorisation:
 * the flip re-runs every check server-side regardless, so a stale rehearsal
 * cannot license a move the server would now refuse.
 *
 * ── PROVIDER-AGNOSTIC ──────────────────────────────────────────────────────
 * Source and target names arrive as props, derived from the migration itself.
 * No provider, driver or direction is named in this file, so R2 -> S3,
 * S3 -> R2, LOCAL -> R2 and R2 -> LOCAL all render identically.
 */

export interface OwnershipTransferPanelProps {
  migrationId: string;
  /** Display name of the provider that owns the assets now. Never hardcoded. */
  sourceName: string;
  /** Display name of the provider that must own them before finalization. */
  targetName: string;
  /** The server's ownership tally, or null if this server did not report one. */
  ownership: ProviderMigrationOwnershipStateDto | null;
  /** True when the caller holds `platform:system:manage`. Gates the flip only. */
  canManage: boolean;
  /**
   * Called with the server's result after a successful flip.
   *
   * The page owns the outcome, not this panel: reloading the migration re-derives
   * the workflow and unmounts this panel, so a success message held here would be
   * destroyed the moment it was earned.
   */
  onFlipped: (result: ProviderMigrationOwnershipResultDto) => void;
}

export function OwnershipTransferPanel({
  migrationId,
  sourceName,
  targetName,
  ownership,
  canManage,
  onFlipped,
}: OwnershipTransferPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ProviderMigrationOwnershipResultDto | null>(null);
  const [error, setError] = useState<ParsedMigrationError | null>(null);

  const busy = submitting || previewing;

  /** The ONLY call site for the read-only rehearsal. Writes nothing. */
  const runPreview = async () => {
    if (busy) return; // no duplicate submissions
    setPreviewing(true);
    setError(null);
    try {
      setPreview(await previewProviderMigrationOwnership(migrationId));
    } catch (err) {
      setPreview(null);
      setError(parseMigrationError(err));
    } finally {
      setPreviewing(false);
    }
  };

  /**
   * The ONLY call site for the ownership flip. Reachable solely from the confirm
   * button inside the dialog, which is itself reachable solely from an explicit
   * click.
   */
  const confirmFlip = async () => {
    if (busy) return; // no duplicate submissions
    setSubmitting(true);
    setError(null);
    try {
      const r = await flipProviderMigrationOwnership(migrationId);
      setConfirming(false);
      onFlipped(r);
    } catch (err) {
      // The server's refusal is authoritative and is shown in full. No retry
      // loop: a refused transition is a decision for the operator.
      setError(parseMigrationError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const total = ownership?.totalAssets ?? null;
  const onTarget = ownership?.onTarget ?? null;
  const remaining = total !== null && onTarget !== null ? total - onTarget : null;

  return (
    <div
      className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5"
      data-testid="ownership-panel"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-4 w-4 text-amber-600" />
        Ownership must move to <span className="uppercase">{targetName}</span>
      </h2>

      <p className="mt-2 text-sm text-muted-foreground" data-testid="ownership-explainer">
        Objects are <strong className="text-foreground">copied and verified</strong>. Ownership must
        now be moved to <strong className="uppercase text-foreground">{targetName}</strong> before
        this migration can be finalized.
      </p>

      {ownership !== null ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="ownership-tally">
          <strong className="text-foreground">{onTarget}</strong> of{' '}
          <strong className="text-foreground">{total}</strong> objects are owned by{' '}
          <span className="uppercase">{targetName}</span>;{' '}
          <strong className="text-foreground">{remaining}</strong> still owned by{' '}
          <span className="uppercase">{sourceName}</span>.
        </p>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="ownership-unknown">
          This server did not report where the objects are currently owned. Run the read-only
          rehearsal below to find out before changing anything.
        </p>
      )}

      <p className="mt-2 text-sm text-muted-foreground">
        Moving ownership <strong className="text-foreground">deletes nothing</strong>. The{' '}
        <span className="uppercase">{sourceName}</span> copies stay retained, and the transfer can
        still be reversed by an authorized operator until the migration is finalized.
      </p>

      {error && <MigrationErrorNotice error={error} testId="ownership-error" />}

      {preview && (
        <div
          className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3"
          data-testid="ownership-preview-result"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-sky-700">
            <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
            Rehearsal complete — nothing was changed
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {preview.eligible} of {preview.totalItems} objects are eligible ·{' '}
            <strong className="text-foreground">{preview.flipped} would move</strong> ·{' '}
            {preview.alreadyInTargetState} already on{' '}
            <span className="uppercase">{targetName}</span> · {preview.failed} would fail ·{' '}
            {preview.objectsDeleted} deleted.
          </p>
          {preview.failed > 0 && (
            <p className="mt-1 text-xs text-amber-700" data-testid="ownership-preview-failures">
              Would fail:{' '}
              {Object.entries(preview.failuresByReason)
                .map(([reason, count]) => `${reason} × ${count}`)
                .join(', ')}
            </p>
          )}
        </div>
      )}

      {!confirming && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="ownership-preview"
            disabled={busy}
            onClick={() => void runPreview()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {previewing ? (
              <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />
            ) : (
              <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
            )}
            {previewing ? 'Rehearsing…' : 'Preview Ownership Transfer'}
          </button>

          {canManage && (
            <button
              type="button"
              data-testid="ownership-open"
              disabled={busy}
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />
              Move Ownership to {targetName.toUpperCase()}
            </button>
          )}
        </div>
      )}

      {confirming && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ownership-confirm-title"
          className="mt-4 rounded-lg border border-amber-500/40 bg-card p-4"
          data-testid="ownership-confirm"
        >
          <p
            id="ownership-confirm-title"
            className="flex items-center gap-2 text-sm font-semibold text-amber-700"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
            Confirm the ownership transfer
          </p>

          <ul
            className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-foreground"
            data-testid="ownership-confirm-points"
          >
            <li>
              Ownership of {total === null ? 'the verified objects' : `${remaining} object(s)`}{' '}
              moves from <strong className="uppercase">{sourceName}</strong> to{' '}
              <strong className="uppercase">{targetName}</strong>.
            </li>
            <li>
              Only <strong>verified</strong> objects move. The server re-downloads and re-hashes
              every destination object before moving that asset.
            </li>
            <li>
              <strong>Nothing is deleted.</strong> The{' '}
              <span className="uppercase">{sourceName}</span> copies remain retained.
            </li>
            <li>
              This does <strong>not</strong> finalize the migration and does not change its status.
              Finalization stays a separate, explicit action.
            </li>
            <li>
              The transfer can be reversed by an authorized operator until the migration is
              finalized.
            </li>
          </ul>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              data-testid="ownership-confirm-submit"
              disabled={submitting}
              onClick={() => void confirmFlip()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />
              ) : (
                <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5" />
              )}
              {submitting
                ? 'Moving ownership…'
                : `Yes, move ownership to ${targetName.toUpperCase()}`}
            </button>
            <button
              type="button"
              data-testid="ownership-confirm-cancel"
              disabled={submitting}
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
